import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
	FetchBookingPricing,
	FetchTransactionPricing,
	transactionPricingQueryOptions,
} from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { dataToFerryTripBookingDto } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { FerryBookingCreationDto } from 'Models/Dtos/BookingDtos';
import { TransactionPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { isNotNullOrUndefined, stringIsEmpty } from 'Util/TypeGuards';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import {
	FreightBookingWizardData,
	freightBookingWizardIsCompletedForPriceCalculations,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/FreightBookingWizardData';
import { tripSummaryLocationType } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/TripBookingSummaryCard';
import useDeviceDetect from './useDeviceDetect';

/**
 * How old a transaction's pricing may be for this hook's first fetch to take it instead of asking again.
 * The cart (CartSummary) and the booking success page (BookingSuccessOuterWrapper) start that request
 * on mount, alongside fetch-bookings-for-cart, and this hook mounts only once the bookings are in — so
 * the answer it finds was requested for this very page. Bounded so a slow bookings fetch falls back to
 * asking again rather than reusing something old.
 */
const PREFETCHED_PRICING_REUSE_MS = 10_000;

function useFetchBookingPriceBreakdown(
	location: tripSummaryLocationType,
	dependencies: any[],
	transactionId?: string,
	wizardData?: BookingWizardData,
	includeCreditCardFee: boolean = true,
	forceCalculate: boolean = false,
	setPriceInParentComponent?: (price?: number) => void,
) {
	const [data, setData] = useState<TransactionPricingResult | null>(null);
	const { isIpad } = useDeviceDetect();
	const queryClient = useQueryClient();
	// Only the first fetch of a mount may take the page's prefetched pricing (see the effect below).
	// Every later one follows a change — a voucher applied, a fee removed — so it must ask the server
	// again. Read and cleared by the next fetch.
	const initialFetchDone = useRef(false);
	const reusePrefetched = useRef(false);
	// Only the latest request may set the price. Requests overlap whenever the wizard data moves twice
	// in quick succession (a fare type change followed by the seat reconciliation, say), and a slower
	// earlier response landing last left the sidebar pricing a party the booker had already changed.
	const latestRequest = useRef(0);

	// The in-flight sidebar pricing request, so a newer one can cancel it. The guard above already
	// stops a stale answer reaching the screen; this stops it being computed at all. Only the wizard
	// pricing call is cancelled — the cart and check-in reads fire once per screen rather than once
	// per change, so there is nothing to race.
	const inFlight = useRef<AbortController | null>(null);

	const useBookingForPriceCalculation =
		location === 'booking-success' || location === 'check-in' || location === 'cart';

	const wizardDataToRequest = new BookingWizardData(wizardData ?? {});
	if (wizardDataToRequest?.innerWizardData?.type === 'freight') {
		// During a freight booking, the required data may not be ready when this hook is called,
		// And dataToFerryTripBookingDto expects valid input data.
		// So removing the items will prevent it being processed.
		wizardDataToRequest.innerWizardData.items = [];
	}

	const convertedWizardData: FerryBookingCreationDto | null = isNotNullOrUndefined(wizardData)
		? dataToFerryTripBookingDto(wizardDataToRequest, false, location === 'check-in', useBookingForPriceCalculation)
		: null;

	const fetchData = useCallback(async (): Promise<TransactionPricingResult | null> => {
		const reuse = reusePrefetched.current;
		reusePrefetched.current = false;
		latestRequest.current += 1;
		const request = latestRequest.current;
		const apply = (response: TransactionPricingResult | null) => {
			if (request !== latestRequest.current) {
				return;
			}
			setData(response);
			if (setPriceInParentComponent) {
				setPriceInParentComponent(response?.total);
			}
		};

		if (location === 'cart' || location === 'check-in' || location === 'booking-success') {
			const transactionIdToUse = transactionId ?? getFerryBookingTransactionIdFromStorage();
			if (stringIsEmpty(transactionIdToUse)) {
				return null;
			}
			const includeCCFee = (location === 'booking-success' || location === 'check-in') && includeCreditCardFee;
			const response = reuse
				? await queryClient.fetchQuery({
						...transactionPricingQueryOptions(transactionIdToUse, includeCCFee, location === 'check-in'),
						staleTime: PREFETCHED_PRICING_REUSE_MS,
					})
				: await FetchTransactionPricing(transactionIdToUse, includeCCFee, location === 'check-in');
			apply(response);
			return response;
		}
		if (
			isNotNullOrUndefined(wizardData) &&
			dataIsValidToFetchBookingPrice(wizardData) &&
			(forceCalculate || !isIpad)
		) {
			inFlight.current?.abort();
			const controller = new AbortController();
			inFlight.current = controller;

			const response = await FetchBookingPricing(
				convertedWizardData,
				wizardData?.wizardMode === 'ALTERATION',
				controller.signal,
			);

			// An aborted request resolves to null. Returning early leaves the sidebar showing the
			// previous price until the request that replaced this one answers, rather than blanking it
			// on every change.
			if (controller.signal.aborted) {
				return null;
			}

			apply(response);
			return response;
		}
		return null;
	}, [
		location,
		transactionId,
		wizardData,
		includeCreditCardFee,
		forceCalculate,
		isIpad,
		convertedWizardData,
		setPriceInParentComponent,
		queryClient,
	]);

	// `isIpad` gates whether the sidebar price breakdown is fetched (see fetchData), so the price is
	// skipped while on a mobile/iPad-sized screen. Without it in the deps, resizing back to a desktop
	// size never re-runs the fetch and the summary stays stuck on "Loading cart".
	useEffect(() => {
		reusePrefetched.current = !initialFetchDone.current && (location === 'cart' || location === 'booking-success');
		initialFetchDone.current = true;
		fetchData();
	}, [...dependencies, isIpad]);

	// Leaving the wizard mid-price should not leave the server pricing a booking nobody is looking at.
	useEffect(() => () => inFlight.current?.abort(), []);

	return { data, refetch: fetchData };
}

// We only want to fetch the booking price if the wizard data is valid
export function dataIsValidToFetchBookingPrice(wizardData: BookingWizardData) {
	if (wizardData.innerWizardData.type === 'vehicle') {
		return true;
	}
	if (wizardData.innerWizardData.type === 'passenger') {
		return true;
	}
	if (wizardData.innerWizardData.type === 'freight') {
		return freightBookingWizardIsCompletedForPriceCalculations(
			wizardData.innerWizardData as FreightBookingWizardData,
		);
	}
	return false;
}

export default useFetchBookingPriceBreakdown;
